import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as ScrollLink } from 'react-scroll';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { useLanguage } from '../contexts/LanguageContext';
import StoreButtons from './StoreButtons';

export default function Hero() {
  const { translations } = useLanguage();

  return (
    <Box
      id="hero"
      sx={{
        width: '100%',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at top left, rgba(124, 58, 237, 0.18), transparent 34%), radial-gradient(circle at bottom right, rgba(79, 70, 229, 0.12), transparent 32%), linear-gradient(180deg, rgba(249, 247, 255, 0.96) 0%, rgba(255, 255, 255, 1) 62%)',
      }}
    >
      <Container
        sx={{
          minHeight: { xs: 'auto', md: 'calc(100vh - 120px)' },
          display: 'grid',
          alignItems: 'center',
          gap: { xs: 4, md: 8 },
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(320px, 0.85fr)' },
          pt: { xs: 10, sm: 14, md: 18 },
          pb: { xs: 8, sm: 11, md: 14 },
        }}
      >
        <Stack spacing={{ xs: 2.5, md: 3.5 }} sx={{ maxWidth: 760 }}>
          <Chip
            label={translations.hero_section.badge}
            variant="outlined"
            sx={{
              alignSelf: 'flex-start',
              borderRadius: 999,
              px: 1,
              fontWeight: 700,
              letterSpacing: 0.2,
              color: 'primary.dark',
              bgcolor: 'rgba(124,58,237,0.08)',
              borderColor: 'rgba(124,58,237,0.16)',
            }}
          />
          <Typography
            component="h1"
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '4rem', md: '5.1rem' },
              lineHeight: { xs: 1.02, md: 0.98 },
              letterSpacing: '-0.05em',
              maxWidth: 780,
            }}
          >
            {translations.hero_section.title}
          </Typography>
        </Stack>

        <Stack
          spacing={{ xs: 2.25, md: 3 }}
          sx={{
            maxWidth: 440,
            justifySelf: { xs: 'stretch', md: 'end' },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'text.secondary',
              lineHeight: { xs: 1.6, md: 1.7 },
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.15rem', md: '1.5rem' },
            }}
          >
            {translations.hero_section.description}
          </Typography>
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              p: 2.25,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.8)',
              border: '1px solid',
              borderColor: 'rgba(124,58,237,0.12)',
              boxShadow: '0 18px 40px rgba(76, 29, 149, 0.08)',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.08em' }}>
                {translations.pricing_section.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {translations.pricing_section.description}
              </Typography>
              <StoreButtons compact />
            </Stack>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <ScrollLink to="pricing" smooth duration={500} offset={-88}>
              <Button size="large" variant="contained" endIcon={<ArrowOutwardRoundedIcon />}>
                {translations.hero_section.primary_cta}
              </Button>
            </ScrollLink>
            <ScrollLink to="contact" smooth duration={500} offset={-88}>
              <Button size="large" variant="outlined">
                {translations.hero_section.secondary_cta}
              </Button>
            </ScrollLink>
          </Stack>
          <Box
            sx={{
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              {translations.hero_section.supporting_text}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
